import React, {FC, RefObject, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {findAncestorByTag, isElement} from '../../../../libs/shared/helpers/src/browser/domHelpers';
import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {tryParseUrl} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {FrontEvent, Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Tooltip} from '../../../../libs/shared/ui/src/components/tooltips/tooltip';
import {TooltipShortcutFormatter} from '../../../../libs/shared/ui/src/components/tooltips/tooltipShortcutFormatter';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {useTimeout} from '../../../../srcCommon/helpers/hookHelpers';

/*
 * Props.
 */

interface LinkTooltipsProps {
  className?: string;
  showKeyboardShortcut?: boolean;
}

/*
 * Constants.
 */

// Avoid unintentionally bringing up tooltips when moving the pointer over a message.
const showTooltipIntentDelay = 500; // ms

// Keep tooltip visible when moving quickly from one link to another.
const hideTooltipIntentDelay = 50; // ms

/*
 * Style.
 */

const StyledTooltipDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas: 'url keyboard-shortcut';
  grid-column-gap: 4px;
`;

const StyledUrlDiv = styled.div`
  grid-area: url;
  ${ellipsis()};
`;

const StyledKeyboardShortcutDiv = styled.div`
  grid-area: keyboard-shortcut;
`;

const StyledGreySpan = styled.span`
  color: ${(p) => p.theme.alphas.white50};
`;

/*
 * Component.
 */

export const LinkTooltips: FC<LinkTooltipsProps> = (props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [activeAnchor, setActiveAnchor] = useState<HTMLAnchorElement | null>(null);

  // Show/hide the tooltip when the active anchor changes.
  const [isTooltipVisible, showTooltip, hideTooltip] = useTooltipIntent();
  // Note: we're using this as a change event handler, since we don't want show/hideTooltip to be effect dependencies.
  useEffect(onActiveAnchorChange, [activeAnchor]);

  // Because React doesn't support these events yet.
  useNativeEventListener(wrapperRef, 'focusin', onHoverOrFocusIn);
  useNativeEventListener(wrapperRef, 'focusout', onHoverOrFocusOut);

  function onActiveAnchorChange() {
    if (activeAnchor) {
      showTooltip();
    } else {
      hideTooltip();
    }
  }

  function onHoverOrFocusIn(event: FrontEvent) {
    const anchor = findAnchorWithEvent(wrapperRef, event);
    setActiveAnchor(anchor);
  }

  function onHoverOrFocusOut() {
    setActiveAnchor(null);
  }

  function onMouseUp(event: FrontEvent) {
    const anchor = findAnchorWithEvent(wrapperRef, event);

    if (anchor) {
      anchor.blur();
    }
  }

  const {className, showKeyboardShortcut, children} = props;
  return (
    // We are following accessibility guidelines by listening for focusin/focusout above.
    /* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events, jsx-a11y/no-static-element-interactions */
    <div
      ref={wrapperRef}
      className={className}
      onMouseOver={onHoverOrFocusIn}
      onMouseOut={onHoverOrFocusOut}
      onMouseUp={onMouseUp}
    >
      {children}
      {activeAnchor && isTooltipVisible && renderTooltip(activeAnchor, showKeyboardShortcut)}
    </div>
  );
};

/** Provides "hover intent" behavior for showing and hiding tooltip. */
function useTooltipIntent(): [boolean, Handler, Handler] {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [setTimeout] = useTimeout();

  function showTooltip() {
    setTimeout(() => setIsTooltipVisible(true), showTooltipIntentDelay);
  }

  function hideTooltip() {
    setTimeout(() => setIsTooltipVisible(false), hideTooltipIntentDelay);
  }

  return [isTooltipVisible, showTooltip, hideTooltip];
}

function findAnchorWithEvent(
  wrapperRef: RefObject<HTMLElement | null>,
  event: FrontEvent,
): HTMLAnchorElement | null {
  const wrapper = wrapperRef.current;
  if (!wrapper) {
    return null;
  }

  const {target} = event;
  if (!isElement(target)) {
    return null;
  }

  return findAncestorByTag(target, wrapper, 'a');
}

function renderTooltip(anchor: HTMLAnchorElement, showKeyboardShortcut = false) {
  const href = anchor.getAttribute('href');
  if (!href) {
    return null;
  }

  return (
    <Tooltip anchor={{current: anchor}}>
      <StyledTooltipDiv>
        {renderUrl(href)}
        {showKeyboardShortcut && renderKeyboardShortcut()}
      </StyledTooltipDiv>
    </Tooltip>
  );
}

function renderUrl(href: string) {
  // Try to parse the provided URL.
  const url = tryParseUrl(href);
  if (!url) {
    return null;
  }

  // Email link.
  if (url.protocol === 'mailto:') {
    return (
      <StyledUrlDiv>
        <StyledGreySpan>{url.protocol}</StyledGreySpan>
        <span>{url.pathname}</span>
      </StyledUrlDiv>
    );
  }

  // Web link.
  return (
    <StyledUrlDiv>
      <StyledGreySpan>{`${url.protocol}//`}</StyledGreySpan>
      <span>{url.host}</span>
      <StyledGreySpan>
        {url.pathname}
        {url.search}
      </StyledGreySpan>
    </StyledUrlDiv>
  );
}

function renderKeyboardShortcut() {
  return (
    <StyledKeyboardShortcutDiv>
      <TooltipShortcutFormatter action={KeyboardActionsEnum.OPEN_LINK} />
    </StyledKeyboardShortcutDiv>
  );
}

/*
 * Hooks.
 */

/**
 * Adds/removes a native event listener to an element.
 *
 * This will register only once and does not handle type/ref changes.
 * Use only for events that React doesn't support yet!
 */
function useNativeEventListener(targetRef: RefObject<Node | null>, type: string, listener: EventListener) {
  // Keep a ref to the latest version of the listener.
  const listenerRef = useRef(listener);
  listenerRef.current = listener;

  useInitialEffect(() => {
    const target = targetRef.current;
    if (!target) {
      throw new Error('A valid ref should be provided.');
    }

    // Wrap the call to the listener ref into a stable handler.
    const stableListener: EventListener = (...args: Parameters<EventListener>) =>
      listenerRef.current(...args);
    target.addEventListener(type, stableListener);

    return () => {
      target.removeEventListener(type, stableListener);
    };
  });
}
